import { makeStyles } from '@material-ui/core/styles';

// Palette lifted from the BCM-897 deck screenshots.
const INK = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e8eaed';
// #f9f9fc is the header fill the other tables use (InactiveBatchHistory, the PD
// batch tabs), so this table's head reads the same as theirs.
const HEADER_BG = '#f9f9fc';
const LINK = '#1976d2';
const NAVY = '#2d1b4e';

export const useStyles = makeStyles((theme) => ({
  root: {
    padding: '0 1.5rem 1.5rem',
    fontFamily: theme.typography.fontFamily,
    background: '#fff',
    // ONE scrollbar on this screen and it belongs to the table. Nothing here is
    // height-capped: PoIssuesTable measures its own container against the
    // window and caps itself, so the page has nothing left to scroll.
  },

  /* ---- tabs ---- */
  tabsBar: {
    // Fixed-height rows in the flex column; only the table flexes.
    flexShrink: 0,
    borderBottom: `1px solid ${BORDER}`,
    marginBottom: '1.25rem',
  },
  tabsRoot: {
    minHeight: 48,
  },
  tab: {
    textTransform: 'uppercase',
    fontSize: '0.875rem',
    fontWeight: 500,
    letterSpacing: '0.04em',
    minWidth: 'auto',
    marginRight: '2rem',
    padding: '0.75rem 0.25rem',
    color: MUTED,
    '&.Mui-selected': {
      color: LINK,
      fontWeight: 600,
    },
  },
  tabIndicator: {
    height: 3,
    backgroundColor: LINK,
  },

  /* ---- filter row ---- */
  // Grouping the controls into a panel separates "how you narrow the data" from
  // the data itself, instead of the two floating against the same white page.
  filterBar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1.25rem',
    flexWrap: 'wrap',
    marginBottom: '1rem',
    padding: '0.875rem 1rem',
    background: '#fafbfc',
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
  },
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  // Field labels are chrome, not content — same treatment as the column heads.
  filterLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: MUTED,
  },
  select: {
    width: 200,
    background: '#fff',
    borderRadius: 4,
    '& .MuiOutlinedInput-input': {
      padding: '9px 32px 9px 12px',
      fontSize: '0.875rem',
      color: INK,
    },
    '& .MuiSelect-select': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  // Free-text PO Number search, sized and padded to match the Selects and the
  // product Autocomplete beside it.
  poNumberInput: {
    width: 180,
    background: '#fff',
    borderRadius: 4,
    '& .MuiOutlinedInput-input': {
      padding: '9px 12px',
      fontSize: '0.875rem',
      color: INK,
    },
  },
  // Padding overrides keep the Autocomplete the same height as the plain
  // Selects beside it.
  productSelect: {
    width: 240,
    background: '#fff',
    borderRadius: 4,
    '& .MuiOutlinedInput-root': {
      padding: '1px 9px',
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 4px',
      fontSize: '0.875rem',
      color: INK,
    },
    '& .MuiAutocomplete-endAdornment': {
      right: 6,
    },
  },
  clearLink: {
    fontSize: '0.875rem',
    color: LINK,
    cursor: 'pointer',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    paddingBottom: 9,
    '&:hover': { color: '#125aa0' },
  },
  countLabel: {
    marginLeft: 'auto',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: INK,
    paddingBottom: 9,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },

  /* ---- table ---- */
  tableWrap: {
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    // Lifts the table off the page so it reads as one surface.
    boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    background: '#fff',
    // The PAGE is the only vertical scrollbar: nothing here caps the height and
    // overflowY is never set, so this can never become a second scrollport.
    //
    // overflowX is scoped below the table's min width. Any overflow value makes
    // an element a scroll container, which would stop the sticky header pinning
    // to the page — so on wider screens it is deliberately left unset and the
    // header follows you down the page instead.
    overflow: 'visible',
    [theme.breakpoints.down(1240)]: {
      overflowX: 'auto',
    },
  },
  table: {
    minWidth: 1180,
  },
  // Column headings read as labels, not data: uppercase, tracked out, muted.
  // The shadow keeps rows from bleeding into the header once it is pinned.
  /* Bold black sentence-case on a light fill, matching the other tables in the
     app (InactiveBatchHistory, PODetails, the PD batch tabs) and the deck's own
     header row. This was 12px uppercase grey with wide tracking, which made it
     the only table header in the product that looked like this. */
  th: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: '#000',
    background: HEADER_BG,
    borderBottom: `1px solid ${BORDER}`,
    padding: '0.8rem 1rem',
    whiteSpace: 'nowrap',
    position: 'sticky',
    // Pins below the fixed app bar, not at the viewport top — at top: 0 the
    // header would slide behind it (the app bar sits on a much higher z-index)
    // and vanish. 64px is theme.mixins.toolbar's height on sm and up.
    top: 64,
    zIndex: 2,
    boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
  },
  td: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    color: INK,
    borderBottom: `1px solid #f1f2f4`,
    padding: '0.875rem 1rem',
    verticalAlign: 'top',
  },
  // Scanning a long table is much easier with a row highlight to follow.
  row: {
    transition: 'background-color 0.12s ease',
    '&:hover': { backgroundColor: '#f8f9fb' },
    '&:last-child > td': { borderBottom: 'none' },
  },
  poNumber: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: INK,
    // Keeps the digits in a column from ragging against each other.
    fontVariantNumeric: 'tabular-nums',
  },
  // Marked for delete in SAP. Same red chip the Inactive POs dashboard uses for
  // a cancelled PO (InactivePOTable's poNumberCancelled), so the two screens
  // flag the same condition the same way.
  poNumberDeleted: {
    backgroundColor: '#d32f2f',
    color: '#ffffff',
    padding: '4px 8px',
    borderRadius: 4,
    display: 'inline-block',
  },
  // Delivery complete — the grey chip from the same screen
  // (poNumberDeliveryComplete). Deliberately not red: a completed delivery is a
  // normal end state, not a problem.
  poNumberDeliveryComplete: {
    backgroundColor: '#5A5A5A',
    color: '#ffffff',
    padding: '4px 8px',
    borderRadius: 4,
    display: 'inline-block',
  },
  // The alert type is what people scan this column for, so it carries weight.
  issueText: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: INK,
    lineHeight: 1.4,
  },

  /* ---- "More Information" chips ---- */
  /* Two columns — labels, then values — so every value in a cell starts at the
     same x whatever its label's width. As a flex column per row the values sat
     at ragged left edges (PLANT vs PO SLOC vs MD SLOC each pushed its value out
     differently), which is what made the column look untidy. */
  infoList: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    columnGap: 10,
    rowGap: 6,
    alignItems: 'baseline',
  },
  // A detail with no label (a plain sentence) takes the full cell width.
  infoFullWidth: {
    gridColumn: '1 / -1',
  },
  chip: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    borderRadius: 3,
    padding: '3px 7px',
    whiteSpace: 'nowrap',
    // Keeps the two-character PO / MD chips from reading as specks next to the
    // wider ones, so the label column looks deliberate rather than accidental.
    minWidth: 46,
    textAlign: 'center',
    lineHeight: 1.4,
    justifySelf: 'start',
  },
  chipPo: { background: '#fdeaea', color: '#c0392b' },
  chipMd: { background: '#e6f4ea', color: '#1e7e34' },
  chipNeutral: { background: '#f1f2f4', color: '#4b5563' },
  infoValue: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    // Break at spaces first and only split a token when it genuinely cannot
    // fit — break-word alone chopped material descriptions mid-word.
    wordBreak: 'normal',
    overflowWrap: 'break-word',
    // Codes and quantities line up column-wise instead of drifting.
    fontVariantNumeric: 'tabular-nums',
  },
  valuePo: { color: '#c0392b' },
  valueMd: { color: '#1e7e34' },
  valueNeutral: { color: INK },
  processFlowLink: {
    fontSize: '0.8125rem',
    color: LINK,
    textDecoration: 'underline',
    cursor: 'pointer',
    display: 'inline-block',
    marginTop: 4,
    lineHeight: 1.4,
    background: 'none',
    border: 'none',
    padding: 0,
    textAlign: 'left',
    fontFamily: 'inherit',
    '&:hover': { color: '#125aa0' },
  },

  /* ---- product cell ---- */
  productCode: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: INK,
    display: 'block',
  },
  productDesc: {
    fontSize: '0.8125rem',
    color: MUTED,
    lineHeight: 1.4,
    display: 'block',
    marginTop: 2,
  },

  /* ---- PO status pill ---- */
  // Was minWidth 150, which made the status the widest thing in every row and
  // pushed the columns either side apart. Sized to its text instead.
  statusPill: {
    display: 'inline-block',
    minWidth: 92,
    textAlign: 'center',
    padding: '5px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  /* PO status colours are the PO dashboard's own palette (renderPOStatusButton
     in PurchaseOrderDashbaordIndex/AuditReportNew.jsx), so a status reads the
     same on both screens. Keyed by statusID in PoIssuesTable. */
  statusCreated: { background: '#B4B4B4' },   // 21 Created
  statusShared: { background: '#2196F3' },    // 22 New PO / Shared with CMO
  statusUnderConf: { background: '#E78A00' }, // 23 Under Confirmation
  statusRejected: { background: '#FF0000' },  // 24 Rejected by CMO
  statusAgreed: { background: '#329429' },    // 25 Confirmed, 27 Approved, 29, 31
  statusAwaiting: { background: '#EC6535' },  // 26 Awaiting Approval
  statusComplete: { background: '#707070' },  // 30 Delivery Complete
  statusUnknown: { background: '#1E1E1E' },   // same fallback the PO dashboard uses

  /* ---- timestamp / resolved-by ---- */
  stampLine: {
    fontSize: '0.8125rem',
    color: INK,
    lineHeight: 1.5,
    display: 'block',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  // The time is secondary to the date, so it reads one step quieter.
  stampTime: {
    fontSize: '0.8125rem',
    color: MUTED,
    lineHeight: 1.5,
    display: 'block',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  // Placeholder for a cell with genuinely nothing to show.
  emptyCell: {
    color: '#b6bac1',
    fontSize: '0.875rem',
  },
  resolvedName: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: INK,
    display: 'block',
  },
  resolvedStamp: {
    fontSize: '0.8125rem',
    color: MUTED,
    lineHeight: 1.5,
    display: 'block',
    whiteSpace: 'nowrap',
  },

  /* ---- action ---- */
  resolveBtn: {
    background: NAVY,
    color: '#fff',
    textTransform: 'none',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 20,
    padding: '6px 14px',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { background: '#3d2765', boxShadow: 'none' },
  },

  emptyRow: {
    textAlign: 'center',
    color: MUTED,
    fontSize: '0.875rem',
    padding: '4rem 0',
  },
  // The tab label stays the word; the count sits behind it, not level with it.
  tabCount: {
    fontWeight: 500,
    color: MUTED,
    marginLeft: 4,
  },

  /* ---- process flow config dialog ---- */
  dialogPaper: {
    maxWidth: 1240,
    width: '95%',
    borderRadius: 8,
  },
  dialogHead: {
    display: 'flex',
    // flex-start, not center: the title block is now two lines.
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '1.25rem 1.5rem 1rem',
  },
  dialogTitle: {
    fontSize: '1.375rem',
    fontWeight: 700,
    color: INK,
  },
  dialogBody: {
    padding: '0 1.5rem 1.5rem',
  },
  configTableWrap: {
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    maxHeight: '65vh',
    overflow: 'auto',
  },
  configTh: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: MUTED,
    background: HEADER_BG,
    borderBottom: `1px solid ${BORDER}`,
    padding: '0.7rem 1rem',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    lineHeight: 1.4,
  },
  configTd: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.8125rem',
    color: INK,
    borderBottom: '1px solid #f1f2f4',
    padding: '0.6rem 1rem',
    whiteSpace: 'nowrap',
  },
  dialogSubtitle: {
    fontSize: '0.8125rem',
    color: MUTED,
    marginTop: 2,
  },
  configCenter: {
    textAlign: 'center',
  },
  // 27 rows of near-identical values: banding and hover are what stop the eye
  // losing its place mid-row.
  configRow: {
    '&:hover': { backgroundColor: '#eef4fb' },
    '&:last-child > td': { borderBottom: 'none' },
  },
  configRowAlt: {
    backgroundColor: '#fcfcfd',
  },
  // TRUE / FALSE as badges — the value is scannable without reading the word.
  configFlag: {
    display: 'inline-block',
    minWidth: 54,
    padding: '2px 8px',
    borderRadius: 3,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
  },
  configFlagTrue: {
    background: '#e6f4ea',
    color: '#1e7e34',
  },
  configFlagFalse: {
    background: '#f1f2f4',
    color: '#5c636b',
  },
  // A blank is a real state here ("unconstrained"), not missing data.
  configBlank: {
    color: '#c6cad1',
  },
  configFlow: {
    display: 'inline-block',
    minWidth: 24,
    fontWeight: 700,
    color: NAVY,
    fontVariantNumeric: 'tabular-nums',
  },
  configRegion: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 3,
    background: '#eef2f7',
    color: '#3c4a5c',
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
  },
  /* ---- loading skeleton ----
     Rendered into the real table body so the header, sticky behaviour, column
     widths and border frame are the live ones. Each cell mimics the shape of the
     content it stands in for, rather than one generic bar per column.
     Hand-rolled because @material-ui/lab's Skeleton is not used anywhere in this
     app and only exists from v4.0.0-alpha.34. */
  '@keyframes poAlertsShimmer': {
    '0%': { backgroundPosition: '-400px 0' },
    '100%': { backgroundPosition: '400px 0' },
  },
  skBar: {
    display: 'block',
    height: 12,
    borderRadius: 6,
    background: '#eceef1',
    backgroundImage:
      'linear-gradient(90deg, #eceef1 0px, #f4f5f7 180px, #eceef1 400px)',
    backgroundSize: '800px 100%',
    backgroundRepeat: 'no-repeat',
    animation: '$poAlertsShimmer 1.4s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  // Stacked lines, e.g. Product (code + description) or Timestamp (date + time).
  skStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // Chip + value on one line, as the "More Information" cell renders.
  skInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  skChip: {
    width: 52,
    height: 16,
    borderRadius: 3,
    flexShrink: 0,
  },
  // Matches statusPill: minWidth 150, radius 4, ~32px tall.
  skPill: {
    width: 150,
    height: 32,
    borderRadius: 4,
  },
  // Matches resolveBtn: pill button, radius 20.
  skButton: {
    width: 132,
    height: 30,
    borderRadius: 20,
  },
  // Inline notice above the table when a resolve attempt fails.
  resolveError: {
    marginBottom: '0.75rem',
    padding: '0.6rem 0.9rem',
    borderRadius: 4,
    background: '#fdeaea',
    border: '1px solid #f5c6c6',
    color: '#a02b22',
    fontSize: '0.8125rem',
  },
  loadErrorText: {
    padding: '3rem 1rem',
    textAlign: 'center',
    fontSize: '0.9375rem',
    color: '#c62828',
  },
}));
